// The trades beside the lineup (B-316, DIRECTION U21): New trade, the offers
// waiting on me, the ones I sent and those under review, and the league's
// trade block. A trade opens in the panel with its own actions, the lineup
// still beside it; Back returns to the list. History, my own block and the
// rules stay on the Trades screen, a tap away at the panel's head.
//
// The same read and the same actions as the Trades screen (`useTrades`,
// `useTradeActions`), so a trade answered here is answered there.

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { TradeView, TradesView } from '@fsp/domain';
import { useTrades } from '../../data/hooks';
import { useTradeActions } from '../../data/trades';
import { EmptyState, TeamMark } from '../../kit';
import { Button, Caption, Chip, ListRow, RowSub, Text } from '../../kit/ui';
import { FromLeague } from '../shared/FromLeague';
import { PlayerName, TeamName } from '../shared/Names';
import { pickShort } from '../shared/pickLabel';
import { STATUS_LABEL, statusTone, TradeDetail } from '../shared/TradeDetail';
import { TradeBuilder, type BuilderSeed } from '../shared/TradeBuilder';

/** A trade for one player, asked from a card beside the lineup ("Trade for"). */
export interface TradeAsk {
  teamId: string;
  playerId: string;
}

export function TradesPanel({ ask, onAsked }: { ask: TradeAsk | null; onAsked: () => void }) {
  return (
    <FromLeague snapshot={useTrades()}>
      {(view) => <TradesPanelBody view={view} ask={ask} onAsked={onAsked} />}
    </FromLeague>
  );
}

function TradesPanelBody({
  view,
  ask,
  onAsked,
}: {
  view: TradesView;
  ask: TradeAsk | null;
  onAsked: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [builder, setBuilder] = useState<BuilderSeed | null>(null);
  const myBlock = useMemo(
    () =>
      view.block.filter((e) => e.isMine).map((e) => ({ playerId: e.player.id, wants: e.wants })),
    [view.block],
  );
  const actions = useTradeActions(view.league.id, view.myTeamId || null, myBlock);
  const all = useMemo(() => [...view.forMe, ...view.sent, ...view.review, ...view.history], [view]);
  const selected = all.find((t) => t.id === selectedId) ?? null;

  // A card's Trade for, beside the lineup: the builder with his owner and him in it.
  useEffect(() => {
    if (ask === null) return;
    setBuilder({
      open: true,
      partnerTeamId: ask.teamId,
      get: [ask.playerId],
      give: [],
      myDrops: [],
      counterOf: null,
    });
    onAsked();
  }, [ask, onAsked]);

  const counter = (t: TradeView) => {
    const [proposer, receiver] = t.sides;
    setSelectedId(null);
    setBuilder({
      open: true,
      partnerTeamId: proposer.team.id,
      give: receiver.players.map((p) => p.player.id),
      get: proposer.players.map((p) => p.player.id),
      givePicks: [...receiver.picks],
      getPicks: [...proposer.picks],
      myDrops: [],
      counterOf: t.id,
    });
  };

  const row = (t: TradeView) => {
    const [a, b] = t.sides;
    return (
      <ListRow
        key={t.id}
        lead={
          <span className="fsp-trade-marks">
            <TeamMark team={a.team} size={20} />
            <TeamMark team={b.team} size={20} />
          </span>
        }
        leadSize="lg"
        tall
        onClick={() => setSelectedId(t.id)}
        cover="Open the trade"
        trail={<Chip tone={statusTone(t.status)}>{STATUS_LABEL[t.status]}</Chip>}
      >
        <Text block>
          {[a, b].map((s, i) => (
            <span key={s.team.id}>
              {i === 1 ? ' · ' : null}
              <Text weight="strong">
                {s.team.isMine ? 'You' : <TeamName team={s.team}>{s.team.ownerName}</TeamName>}
              </Text>
              {s.team.isMine ? ' give ' : ' gives '}
              {s.players.length === 0 && s.draftPicks.length === 0
                ? 'nothing'
                : s.players.map((p, j) => (
                    <Fragment key={p.player.id}>
                      {j > 0 ? ', ' : ''}
                      <PlayerName player={p.player} club={p.player.proTeam}>
                        {p.player.shortName}
                      </PlayerName>
                    </Fragment>
                  ))}
              {s.draftPicks.map((p, j) => (
                <Fragment key={p.id}>
                  {j > 0 || s.players.length > 0 ? ', ' : ''}
                  {pickShort(p)}
                </Fragment>
              ))}
            </span>
          ))}
        </Text>
        <RowSub>{t.timeLabel}</RowSub>
      </ListRow>
    );
  };

  const groups: { id: string; label: string; rows: TradeView[] }[] = [
    { id: 'forMe', label: 'Waiting on you', rows: view.forMe },
    { id: 'sent', label: 'You sent', rows: view.sent },
    { id: 'review', label: 'Under review', rows: view.review },
  ];
  const others = view.block.filter((e) => !e.isMine);

  return (
    <div className="fsp-side-list">
      {selected !== null ? (
        <div className="fsp-side-detail">
          <div className="fsp-side-back">
            <Button size="xs" variant="quiet" icon="back" onClick={() => setSelectedId(null)}>
              All trades
            </Button>
          </div>
          <TradeDetail
            key={selected.id}
            trade={selected}
            actions={actions}
            onCounter={counter}
            onDone={() => setSelectedId(null)}
          />
        </div>
      ) : (
        <>
          <div className="fsp-side-intro">
            <div className="fsp-sub fsp-ui-wrap-text">
              {view.header.closedReason ?? view.header.deadlineLabel}
            </div>
            <Button
              variant="primary"
              size="xs"
              icon="add"
              disabled={!view.header.open}
              onClick={() =>
                setBuilder({
                  open: true,
                  partnerTeamId: null,
                  give: [],
                  get: [],
                  myDrops: [],
                  counterOf: null,
                })
              }
            >
              New trade
            </Button>
          </div>
          {groups.every((g) => g.rows.length === 0) ? (
            <EmptyState>No trades going. The builder is one tap away.</EmptyState>
          ) : (
            groups
              .filter((g) => g.rows.length > 0)
              .map((g) => (
                <Fragment key={g.id}>
                  <div className="fsp-side-section" role="heading" aria-level={3}>
                    <Caption>{g.label}</Caption>
                  </div>
                  {g.rows.map(row)}
                </Fragment>
              ))
          )}
          {/* THE LEAGUE'S BLOCK: who is shopping, and what they want back. An
              offer starts from the row. */}
          {others.length > 0 ? (
            <>
              <div className="fsp-side-section" role="heading" aria-level={3}>
                <Caption>On the block</Caption>
              </div>
              {others.map((e) => (
                <ListRow
                  key={`${e.team.id}-${e.player.id}`}
                  lead={<TeamMark team={e.team} size={20} />}
                  trail={
                    <Button
                      size="xs"
                      variant="soft"
                      disabled={!view.header.open}
                      onClick={() =>
                        setBuilder({
                          open: true,
                          partnerTeamId: e.team.id,
                          give: [],
                          get: [e.player.id],
                          myDrops: [],
                          counterOf: null,
                        })
                      }
                    >
                      Offer
                    </Button>
                  }
                >
                  <Text block>
                    <PlayerName player={e.player} club={e.player.proTeam} />
                  </Text>
                  <RowSub wrap>
                    <TeamName team={e.team}>{e.team.ownerName}</TeamName>
                    {e.wants ? ` · wants ${e.wants}` : ''}
                  </RowSub>
                </ListRow>
              ))}
            </>
          ) : null}
        </>
      )}
      <TradeBuilder seed={builder} actions={actions} onClose={() => setBuilder(null)} />
    </div>
  );
}
